import {useContext, useMemo, useState} from 'react'
import Drawer from '@mui/material/Drawer'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import Collapse from '@mui/material/Collapse'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import Chip from '@mui/material/Chip'
import {SpaceContext} from '../SpaceProvider.tsx'
import {taruviClient} from '../taruviClient.ts'

const AddWidgetDrawer = ({open, onClose}: { open: boolean, onClose: () => void }) => {
  const [widgets, spaceConfig, setSpaceConfig] = useContext(SpaceContext)!
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [selected, setSelected] = useState<string | null>(null)

  const filtered = widgets?.filter((w: any) =>
    w.name.toLowerCase().includes(search.toLowerCase())
  ) || []

  const grouped = useMemo(() => {
    const groups: Record<string, any[]> = {}
    filtered.forEach((w: any) => {
      const key = w.app_name || 'Other'
      if (!groups[key]) groups[key] = []
      groups[key].push(w)
    })
    return groups
  }, [filtered])

  const toggleCollapse = (app: string) => {
    setCollapsed(prev => ({...prev, [app]: !prev[app]}))
  }

  const handleAdd = async (widget: {slug: string, app_slug: string, name: string, description: string, preview_url: string | null}) => {
    setSelected(`${widget.app_slug}:${widget.slug}`)
    const widgetData: any = await taruviClient.httpClient.get(`api/apps/${widget.app_slug}/widgets/${widget.slug}/`)
    const id = `${widget.slug}-${Date.now()}`
    setSpaceConfig((prev: any) => {
      if (prev.some((w: any) => !w.is_removed && w.slug === widget.slug && w.appSlug === widget.app_slug)) return prev
      return [...prev, {
        id, slug: widget.slug,
        appSlug: widget.app_slug,
        react_code_content: widgetData?.data?.react_code_content,
        style_code_content: widgetData?.data?.style_code_content,
        metadata: widgetData?.data?.metadata,
        layouts: {i: id, x: 0, y: Infinity, w: widgetData?.data?.metadata?.width || 2, h: widgetData?.data?.metadata?.height || 2},
      }]
    })
    setTimeout(() => setSelected(null), 300)
  }

  return (
    <Drawer open={open} onClose={onClose} anchor="right"
            PaperProps={{sx: {width: 360}}}>

      <Box sx={{p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <Typography variant="h6" fontWeight={700}>Widget Library</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon/></IconButton>
      </Box>

      <Box sx={{px: 2.5, mb: 2}}>
        <TextField fullWidth size="small" placeholder="Search widgets..."
                   value={search} onChange={e => setSearch(e.target.value)}
                   slotProps={{input: {startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action"/></InputAdornment>}}}
                   sx={{'& .MuiOutlinedInput-root': {borderRadius: 2}}}/>
      </Box>

      <Box sx={{px: 2.5, flex: 1, overflow: 'auto'}}>
        {Object.entries(grouped).map(([appName, appWidgets]) => (
          <Box key={appName} sx={{mb: 2, bgcolor: '#f5f7fa', borderRadius: 3, p: 1.5}}>
            <Box onClick={() => toggleCollapse(appName)}
                 sx={{
                   display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                   cursor: 'pointer', py: 0.5,
                   transition: 'background 0.15s',
                 }}>
              <Typography fontSize={13} fontWeight={700} color="text.secondary" sx={{textTransform: 'uppercase', letterSpacing: 0.5}}>{appName}</Typography>
              {collapsed[appName] ? <ExpandMoreIcon fontSize="small" color="action"/> : <ExpandLessIcon fontSize="small" color="action"/>}
            </Box>
            <Collapse in={!collapsed[appName]} sx={{mt: 1.5}}>
              {appWidgets.map((widget: any) => {
                const isAdded = spaceConfig.some((w: any) => !w.is_removed && w.slug === widget.slug && w.appSlug === widget.app_slug)
                return (
                <Box key={`${widget.app_slug}:${widget.slug}`} onClick={() => { if (!isAdded) handleAdd(widget) }}
                     sx={{
                       mb: 2.5, borderRadius: 3, cursor: isAdded ? 'default' : 'pointer',
                       border: '1px solid',
                       borderColor: selected === `${widget.app_slug}:${widget.slug}` ? 'primary.main' : 'divider',
                       '&:hover': {borderColor: isAdded ? 'divider' : 'primary.main'},
                       overflow: 'hidden',
                     }}>
                  {/* Preview */}
                  <Box sx={{bgcolor: '#f8f9fa', p: 2, display: 'flex', justifyContent: 'center'}}>
                    {widget.preview_url ? (
                      <img src={widget.preview_url} alt={widget.name} style={{width: '100%', borderRadius: 8, objectFit: 'cover'}}/>
                    ) : (
                      <Box sx={{height: 120, width: '100%', borderRadius: 2, position: 'relative', overflow: 'hidden', bgcolor: '#eef2f7'}}>
                        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <pattern id={`p-${widget.slug}`} width="20" height="20" patternUnits="userSpaceOnUse">
                              <circle cx="2" cy="2" r="1.5" fill="#c5cdd8" opacity="0.5"/>
                            </pattern>
                          </defs>
                          <rect width="100%" height="100%" fill={`url(#p-${widget.slug})`}/>
                          <rect x="15%" y="20%" width="70%" height="18%" rx="4" fill="#dce3ec"/>
                          <rect x="15%" y="46%" width="50%" height="10%" rx="3" fill="#dce3ec"/>
                          <rect x="15%" y="62%" width="60%" height="10%" rx="3" fill="#dce3ec"/>
                          <rect x="15%" y="78%" width="35%" height="10%" rx="3" fill="#dce3ec"/>
                        </svg>
                      </Box>
                    )}
                  </Box>
                  {/* Info */}
                  <Box sx={{p: 2}}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
                      <Typography fontWeight={700} fontSize={16}>{widget.name}</Typography>
                      {isAdded && <Chip label="Added" size="small" sx={{height: 22, fontSize: 11}} />}
                    </Box>
                    {widget.description && (
                      <Typography fontSize={13} color="text.secondary" sx={{mt: 0.5}}>{widget.description}</Typography>
                    )}
                    {widget.metadata?.category && (
                      <Box sx={{mt: 1}}>
                        <Chip label={widget.metadata.category} size="small"
                              sx={{height: 24, fontSize: 12, bgcolor: '#f0f4ff', color: '#1976d2', fontWeight: 600}}/>
                      </Box>
                    )}
                  </Box>
                </Box>
                )
              })}
            </Collapse>
          </Box>
        ))}
      </Box>
    </Drawer>
  )
}

export default AddWidgetDrawer
